import { type FC } from 'react';
import styled from 'styled-components';
import GreenArrowUp from '../../../../assets/GreenArrowUp.svg';
import RedArrowDown from '../../../../assets/RedArrowDown.svg';
import NeutralState from '../../../../assets/Line.svg';
import { BehaviorType } from '../../enum/BehaviorType';
import { ScoreBreakdownType, type TeamBehavior } from '../../../../lib/types/TeamBehavior';
import { Tooltip } from '../../../Tooltip';

export const BehaviorBarMap = {
  [BehaviorType.WEAK]: {
    backgroundColor: '#E93354',
    icon: <GreenArrowUp />,
  },
  [BehaviorType.NEUTRAL]: {
    backgroundColor: '#B6BEDC',
    icon: <NeutralState />,
  },
  [BehaviorType.STRONG]: {
    backgroundColor: '#3ABF87',
    icon: <RedArrowDown />,
  },
};

type BehaviorBarProps = {
  teamBehaviorDetails: TeamBehavior;
  totalCount: number;
  onClick?: (behavior: ScoreBreakdownType, type: BehaviorType) => void;
};

export const BehaviorBar: FC<BehaviorBarProps> = ({ teamBehaviorDetails, totalCount, onClick }) => {
  const isZeroState =
    teamBehaviorDetails.learners.strong + teamBehaviorDetails.learners.weak + teamBehaviorDetails.learners.neutral ===
    0;
  const hasStrongBehavior = teamBehaviorDetails.learners.strong > 0;
  const hasWeakBehavior = teamBehaviorDetails.learners.weak > 0;
  const hasOnlyOneBehavior =
    (teamBehaviorDetails.learners.strong > 0 &&
      teamBehaviorDetails.learners.weak === 0 &&
      teamBehaviorDetails.learners.neutral === 0) ||
    (teamBehaviorDetails.learners.strong === 0 &&
      teamBehaviorDetails.learners.weak > 0 &&
      teamBehaviorDetails.learners.neutral === 0) ||
    (teamBehaviorDetails.learners.strong === 0 &&
      teamBehaviorDetails.learners.weak === 0 &&
      teamBehaviorDetails.learners.neutral > 0);

  const getBehaviorTypeProgressBarWithSteps = () => {
    return (
      <ProgressBarWrapper>
        <ProgressBar>
          {isZeroState ? (
            <ProgressFill
              isZeroState={isZeroState}
              progress={50}
              backgroundColor={BehaviorBarMap[BehaviorType.NEUTRAL].backgroundColor}
            />
          ) : (
            Object.entries(teamBehaviorDetails.learners)
              .reverse()
              .map(([key, value], index) => {
                const behaviorMap = BehaviorBarMap[key as BehaviorType];
                const progress = ((value as number) / totalCount) * 100;
                const showProgress = value > 0;
                return (
                  showProgress && (
                    <StyledTooltip
                      key={index}
                      offset={10}
                      content={<TooltipContainer>{value}</TooltipContainer>}
                      placement="top"
                      $width={progress}
                    >
                      <ProgressFill
                        isZeroState={isZeroState}
                        type={key as BehaviorType}
                        hasStrongBehavior={hasStrongBehavior}
                        hasWeakBehavior={hasWeakBehavior}
                        hasOnlyOneBehavior={hasOnlyOneBehavior}
                        key={index}
                        progress={progress}
                        backgroundColor={behaviorMap.backgroundColor}
                        onClick={() =>
                          onClick && !isZeroState && onClick(teamBehaviorDetails.behaviour, key as BehaviorType)
                        }
                      />
                    </StyledTooltip>
                  )
                );
              })
          )}
        </ProgressBar>
      </ProgressBarWrapper>
    );
  };
  return (
    <BehaviorBarContainer>
      <TeamMemberCount>{teamBehaviorDetails.learners.weak}</TeamMemberCount>
      <BaseBarWrapper>
        <BaseBar />
        {getBehaviorTypeProgressBarWithSteps()}
      </BaseBarWrapper>
      <TeamMemberCount>{teamBehaviorDetails.learners.strong}</TeamMemberCount>
    </BehaviorBarContainer>
  );
};
const BehaviorBarContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const BaseBarWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  position: relative;
`;

const BaseBar = styled.div`
  position: absolute;
  min-height: 2px;
  width: 100%;
  background-color: #d5daee;
  z-index: 1;
`;

const ProgressBarWrapper = styled.div`
  width: 100%;
  z-index: 9;
`;

const ProgressBar = styled.div<{ backgroundColor?: string }>`
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 4px;
  flex-grow: 1;
  height: 12px;
  border-radius: 16px;

  @media (max-width: 768px) {
    height: 16px;
  }
`;

const TooltipContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
  padding: 8px 10px;
  min-width: 40px;
  outline: 1px solid ${({ theme }) => theme.colors.prototype.border};
  background-color: ${({ theme }) => theme.colors.prototype.background};
  border-radius: ${({ theme }) => theme.borderRadius.normal};
  box-shadow: ${({ theme }) => theme.shadow.light};
  color: ${({ theme }) => theme.colors.prototype.foreground};
`;

const StyledTooltip = styled(Tooltip)<{ $width: number }>`
  width: ${({ $width }) => ($width ? `${$width}%` : '10px')};
`;

const ProgressFill = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'progress' && prop !== 'backgroundColor',
})<{
  progress: number;
  backgroundColor?: string;
  type?: BehaviorType;
  isZeroState: boolean;
  hasStrongBehavior?: boolean;
  hasWeakBehavior?: boolean;
  hasOnlyOneBehavior?: boolean;
}>`
  width: 100%;
  background: ${({ backgroundColor }) =>
    backgroundColor ? backgroundColor : BehaviorBarMap[BehaviorType.NEUTRAL].backgroundColor};
  transition: width 0.3s ease-in-out;
  border-radius: ${({ type, isZeroState, hasStrongBehavior, hasWeakBehavior, hasOnlyOneBehavior }) =>
    isZeroState || hasOnlyOneBehavior
      ? '8px'
      : type === BehaviorType.STRONG
        ? '0 8px 8px 0'
        : type === BehaviorType.WEAK
          ? '8px 0 0 8px'
          : hasStrongBehavior && hasWeakBehavior
            ? 'none'
            : hasWeakBehavior
              ? '0 8px 8px 0'
              : hasStrongBehavior
                ? '8px 0 0 8px'
                : 'none'};
  cursor: ${({ isZeroState }) => (isZeroState ? 'default' : 'pointer')};

  height: 12px;
  &:hover {
    height: 12px;
  }

  @media (max-width: 768px) {
    height: 16px;

    &:hover {
      height: 16px;
    }
  }
`;

const TeamMemberCount = styled.span`
  font-size: 12px;
  font-weight: 400;
  margin: 0 8px;
  flex-shrink: 0;
`;
